import React, { useEffect, useCallback, memo } from 'react';
import './ProjectModal.css';
import { Project, ProjectTag } from './types';
import ImageCarousel from './components/ImageCarousel';

interface ProjectModalProps {
  project: Project | null;
  isOpen: boolean;
  onClose: () => void;
  getTagStyle: (tag: ProjectTag) => { backgroundColor: string; color: string };
}

const ProjectModal = memo<ProjectModalProps>(({ project, isOpen, onClose, getTagStyle }) => {
  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', handleKeyDown);
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !project) return null;

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content">
        <button className="modal-close" onClick={onClose} aria-label="Close modal">
          ×
        </button>
        
        <div className="modal-header">
          <h2>{project.title}</h2>
          {project.tags && (
            <div className="project-tags">
              {project.tags.map((tag, index) => (
                <span key={index} className="project-tag" style={getTagStyle(tag)}>{tag}</span>
              ))}
            </div>
          )}
          <div className="project-technologies">
            {project.technologies.map((tech, index) => (
              <span key={index} className="tech-tag">{tech}</span>
            ))}
          </div>
        </div>

        <div className="modal-body">
          <div className="project-description" dangerouslySetInnerHTML={{ __html: project.longDescription }} />
                    {project.images && project.images.length > 0 && (
            <div className="project-images">
              {project.images.length === 1 ? (
                <figure className="project-image-container">
                  <img src={project.images[0].path} alt={`${project.title} screenshot`} />
                  <figcaption>{project.images[0].caption}</figcaption>
                </figure>
              ) : (
                <ImageCarousel images={project.images} projectTitle={project.title} />
              )}
            </div>
          )}
        </div>

        {(project.githubUrl || project.liveUrl) && (
          <div className="modal-footer">
            {project.githubUrl && (
              <a href={project.githubUrl} target="_blank" rel="noopener noreferrer" className="project-link">
                GitHub
              </a>
            )}
            {project.liveUrl && (
              <a href={project.liveUrl} target="_blank" rel="noopener noreferrer" className="project-link">
                Live Demo
              </a>
            )}
          </div>
        )}
        
      </div>
    </div>
  );
});

export default ProjectModal;